import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { trackEvent } from '@/lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══ Color Tokens ═══ */
const C = {
  bg: '#131619', surface: '#1A1E23', card: '#1A1E23', border: '#2A2F36',
  blue: '#4389D0', blueL: '#5BA3F5', blueDim: '#1A2840',
  green: '#2D9B65', greenDim: '#071A0E',
  amber: '#CC8B12', amberDim: '#1E1304',
  red: '#D94444', text: '#E4E7EC', muted: '#8B929E', dim: '#2A2F36',
};
const syne = (w = 700) => ({ fontFamily: 'Inter, sans-serif', fontWeight: w });
const mono = (w = 400) => ({ fontFamily: 'Inter, sans-serif', fontWeight: w >= 500 ? 600 : 500, fontVariantNumeric: 'tabular-nums' });
const sans = (w = 400) => ({ fontFamily: 'Inter, sans-serif', fontWeight: w });

/* ═══ i18n ═══ */
const T = {
  en: {
    badge: 'WORLD CUP BRAND CHAMPION PREDICTOR™',
    h1a: 'Which national team will finish the',
    h1b: 'FIFA World Cup 2026',
    h1c: 'as the most valuable football brand in the world?',
    note: 'This is not asking who will win the World Cup. This is asking who will have the highest Brand Value according to the World Cup Brand Index™.',
    selectTeam: 'Select your prediction',
    searchPlaceholder: 'Search nations...',
    stepForm: 'Almost there',
    firstName: 'First Name',
    country: 'Country',
    email: 'Email Address',
    submit: 'Submit Prediction',
    submitting: 'Submitting...',
    emailError: 'Please enter a valid email address.',
    confirmTitle: 'Your prediction has been recorded.',
    confirmSub: 'You predicted',
    confirmAs: 'as the World Cup Brand Champion 2026.',
    rankTitle: 'World Cup Brand Champion Ranking',
    rankVotes: 'votes',
    counterTotal: 'Total Predictions',
    counterCountries: 'Countries Participating',
    counterMost: 'Most Predicted Champion',
    counterFastest: 'Fastest Rising',
    shareTitle: 'Share your prediction',
    shareLinkedin: 'LinkedIn',
    shareX: 'X',
    shareWhatsapp: 'WhatsApp',
    shareText: (team) => `🏆 My prediction for the World Cup Brand Champion 2026 is ${team}.\n\nPowered by World Cup Brand Index™ · sports.yourbrandvalue.com`,
    foundersTitle: 'Meet the Founders',
    foundersBody: "We're Tamara & Alba Navarrete, two sisters building the world's first football brand value platform.\n\nWe created Your Brand Value Sports and World Cup Brand Index™ because we believe football should measure brand value as intelligently as it measures performance.\n\nIf you believe in that vision, we'd love you to be part of the journey.",
    supportTitle: 'Become a Founding Supporter',
    supportBody: 'Help us build the future of football brand intelligence. Every supporter helps us continue improving World Cup Brand Index™ and keep the project independent and accessible to everyone.',
    supportOther: 'Other',
    supportThanks: 'Thank you! Redirecting to secure payment...',
    supportProcessing: 'Verifying your payment...',
    supportSuccess: 'Thank you for your support! You are now a Founding Supporter.',
    supportCancelled: 'Payment was cancelled. You can try again anytime.',
    supportCustom: 'Enter amount (€)',
    supportProof: (n, c) => `${n} Founding Supporters · ${c} countries`,
    supportLegal: 'Contributions support the development of Your Brand Value Sports and World Cup Brand Index™. They are not charitable donations, investments, financial products, and do not provide ownership rights or financial returns.',
    footerCopy: '© 2026 Your Brand Value Sports · World Cup Brand Index™',
    disclaimer: 'World Cup Brand Index™ 2026 is an independent brand intelligence and research tool developed by Your Brand Value Sports. It is not affiliated with, sponsored by, endorsed by, or officially connected to FIFA, the FIFA World Cup™, any national football federation, or any official tournament partner. All brand scores, rankings, valuations and projections are proprietary analytical estimates generated by Your Brand Value Sports and are provided for informational purposes only.',
    langToggle: true,
    back: '← Back',
  },
  es: {
    badge: 'WORLD CUP BRAND CHAMPION PREDICTOR™',
    h1a: '¿Qué selección terminará el',
    h1b: 'Mundial FIFA 2026',
    h1c: 'como la marca de fútbol más valiosa del mundo?',
    note: 'No preguntamos quién ganará el Mundial. Preguntamos quién tendrá el mayor Brand Value según el World Cup Brand Index™.',
    selectTeam: 'Selecciona tu predicción',
    searchPlaceholder: 'Buscar selecciones...',
    stepForm: 'Casi listo',
    firstName: 'Nombre',
    country: 'País',
    email: 'Email',
    submit: 'Enviar Predicción',
    submitting: 'Enviando...',
    emailError: 'Por favor, introduce una dirección de correo válida.',
    confirmTitle: 'Tu predicción ha sido registrada.',
    confirmSub: 'Predijiste',
    confirmAs: 'como World Cup Brand Champion 2026.',
    rankTitle: 'Ranking World Cup Brand Champion',
    rankVotes: 'votos',
    counterTotal: 'Predicciones Totales',
    counterCountries: 'Países Participando',
    counterMost: 'Selección Más Predicha',
    counterFastest: 'Mayor Crecimiento',
    shareTitle: 'Comparte tu predicción',
    shareLinkedin: 'LinkedIn',
    shareX: 'X',
    shareWhatsapp: 'WhatsApp',
    shareText: (team) => `🏆 Mi predicción para el World Cup Brand Champion 2026 es ${team}.\n\nPowered by World Cup Brand Index™ · sports.yourbrandvalue.com`,
    foundersTitle: 'Conoce a las Fundadoras',
    foundersBody: "Somos Tamara y Alba Navarrete, dos hermanas construyendo la primera plataforma global de valor de marca aplicada al fútbol.\n\nCreamos Your Brand Value Sports y World Cup Brand Index™ porque creemos que el fútbol debe medir el valor de marca con la misma inteligencia con la que mide el rendimiento deportivo.\n\nSi compartes esa visión, nos encantará que formes parte de este camino.",
    supportTitle: 'Conviértete en Founding Supporter',
    supportBody: 'Ayúdanos a construir el futuro de la inteligencia de marca aplicada al fútbol. Cada supporter nos ayuda a seguir mejorando World Cup Brand Index™ y a mantener el proyecto independiente y accesible para todos.',
    supportOther: 'Otro importe',
    supportThanks: '¡Gracias! Redirigiendo al pago seguro...',
    supportProcessing: 'Verificando tu pago...',
    supportSuccess: '¡Gracias por tu apoyo! Ya eres Founding Supporter.',
    supportCancelled: 'El pago fue cancelado. Puedes intentarlo en cualquier momento.',
    supportCustom: 'Introduce importe (€)',
    supportProof: (n, c) => `${n} Founding Supporters · ${c} países`,
    supportLegal: 'Las aportaciones ayudan al desarrollo de Your Brand Value Sports y World Cup Brand Index™. No constituyen donaciones benéficas, inversiones, productos financieros ni generan derechos de propiedad o retornos económicos.',
    footerCopy: '© 2026 Your Brand Value Sports · World Cup Brand Index™',
    disclaimer: 'World Cup Brand Index™ 2026 es una herramienta independiente de inteligencia e investigación de marca desarrollada por Your Brand Value Sports. No está afiliada, patrocinada, avalada ni vinculada oficialmente con FIFA, la Copa Mundial de la FIFA™, ninguna federación nacional de fútbol ni ningún patrocinador oficial del torneo. Todos los Brand Scores, rankings, valoraciones y proyecciones son estimaciones analíticas propias de Your Brand Value Sports y se ofrecen únicamente con fines informativos.',
    langToggle: true,
    back: '← Volver',
  },
};

/* ═══ Team Data ═══ */
const TEAMS = [
  {id:"arg",flag:"🇦🇷",en:"Argentina",es:"Argentina"},
  {id:"bra",flag:"🇧🇷",en:"Brazil",es:"Brasil"},
  {id:"fra",flag:"🇫🇷",en:"France",es:"Francia"},
  {id:"esp",flag:"🇪🇸",en:"Spain",es:"España"},
  {id:"eng",flag:"🇬🇧",en:"England",es:"Inglaterra"},
  {id:"por",flag:"🇵🇹",en:"Portugal",es:"Portugal"},
  {id:"ger",flag:"🇩🇪",en:"Germany",es:"Alemania"},
  {id:"bel",flag:"🇧🇪",en:"Belgium",es:"Bélgica"},
  {id:"usa",flag:"🇺🇸",en:"USA",es:"EE.UU."},
  {id:"ned",flag:"🇳🇱",en:"Netherlands",es:"P. Bajos"},
  {id:"nor",flag:"🇳🇴",en:"Norway",es:"Noruega"},
  {id:"mar",flag:"🇲🇦",en:"Morocco",es:"Marruecos"},
  {id:"col",flag:"🇨🇴",en:"Colombia",es:"Colombia"},
  {id:"mex",flag:"🇲🇽",en:"Mexico",es:"México"},
  {id:"jpn",flag:"🇯🇵",en:"Japan",es:"Japón"},
  {id:"kor",flag:"🇰🇷",en:"South Korea",es:"Corea del Sur"},
  {id:"uru",flag:"🇺🇾",en:"Uruguay",es:"Uruguay"},
  {id:"cro",flag:"🇭🇷",en:"Croatia",es:"Croacia"},
  {id:"sen",flag:"🇸🇳",en:"Senegal",es:"Senegal"},
  {id:"tur",flag:"🇹🇷",en:"Türkiye",es:"Türkiye"},
  {id:"egy",flag:"🇪🇬",en:"Egypt",es:"Egipto"},
  {id:"can",flag:"🇨🇦",en:"Canada",es:"Canadá"},
  {id:"swe",flag:"🇸🇪",en:"Sweden",es:"Suecia"},
  {id:"sui",flag:"🇨🇭",en:"Switzerland",es:"Suiza"},
  {id:"ecu",flag:"🇪🇨",en:"Ecuador",es:"Ecuador"},
  {id:"civ",flag:"🇨🇮",en:"Ivory Coast",es:"Costa de Marfil"},
  {id:"aus",flag:"🇦🇺",en:"Australia",es:"Australia"},
  {id:"alg",flag:"🇩🇿",en:"Algeria",es:"Argelia"},
  {id:"irn",flag:"🇮🇷",en:"Iran",es:"Irán"},
  {id:"sau",flag:"🇸🇦",en:"Saudi Arabia",es:"Arabia Saudí"},
  {id:"saf",flag:"🇿🇦",en:"South Africa",es:"Sudáfrica"},
  {id:"qat",flag:"🇶🇦",en:"Qatar",es:"Qatar"},
  {id:"gha",flag:"🇬🇭",en:"Ghana",es:"Ghana"},
  {id:"tun",flag:"🇹🇳",en:"Tunisia",es:"Túnez"},
  {id:"sco",flag:"🏴󠁧󠁢󠁳󠁣󠁴󠁿",en:"Scotland",es:"Escocia"},
  {id:"aut",flag:"🇦🇹",en:"Austria",es:"Austria"},
  {id:"cze",flag:"🇨🇿",en:"Czech Republic",es:"Chequia"},
  {id:"par",flag:"🇵🇾",en:"Paraguay",es:"Paraguay"},
  {id:"pan",flag:"🇵🇦",en:"Panama",es:"Panamá"},
  {id:"irq",flag:"🇮🇶",en:"Iraq",es:"Iraq"},
  {id:"nzl",flag:"🇳🇿",en:"New Zealand",es:"Nueva Zelanda"},
  {id:"jor",flag:"🇯🇴",en:"Jordan",es:"Jordania"},
  {id:"bih",flag:"🇧🇦",en:"Bosnia-Herzegovina",es:"Bosnia-Herzegovina"},
  {id:"hai",flag:"🇭🇹",en:"Haiti",es:"Haití"},
  {id:"cve",flag:"🇨🇻",en:"Cape Verde",es:"Cabo Verde"},
  {id:"cgo",flag:"🇨🇩",en:"DR Congo",es:"Rep. D. Congo"},
  {id:"uzb",flag:"🇺🇿",en:"Uzbekistan",es:"Uzbekistán"},
  {id:"cur",flag:"🇨🇼",en:"Curaçao",es:"Curaçao"},
];

/* ═══ CSS ═══ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
@keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
@keyframes pulse-dot{0%,100%{opacity:1}50%{opacity:.3}}
.pred-fade{animation:fadeUp .5s ease forwards}
.pred-card:hover{border-color:${C.blue}!important;background:${C.blueDim}!important;transform:scale(1.02)}
.pred-lang:hover{background:rgba(67,137,208,.15)!important}
.pred-share:hover{opacity:.85!important;transform:translateY(-1px)}
.pred-amt:hover{border-color:${C.green}!important;color:${C.green}!important}
`;

export default function PredictorPage() {
  const { lang, setLang } = useLanguage();
  const t = T[lang] || T.en;

  const [step, setStep] = useState(1); // 1=select, 2=form, 3=done
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ firstName: '', country: '', email: '' });
  const [emailErr, setEmailErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [supportStats, setSupportStats] = useState({ total_supporters: 0, countries_count: 0 });
  const [supportDone, setSupportDone] = useState(false);
  const [supportStatus, setSupportStatus] = useState(null); // null | 'redirecting' | 'processing' | 'success' | 'cancelled'
  const [customAmount, setCustomAmount] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  // Load results on mount
  useEffect(() => {
    trackEvent('landing_view', { landing_type: 'predictor', language: lang });
    fetch(`${API}/api/predictions/results`).then(r => r.json()).then(setResults).catch(() => {});
    fetch(`${API}/api/supporters/stats`).then(r => r.json()).then(setSupportStats).catch(() => {});
  }, []);

  const teamName = (tm) => tm ? tm[lang] || tm.en : '';
  const filtered = TEAMS.filter(tm => {
    if (!search) return true;
    const q = search.toLowerCase();
    return tm.en.toLowerCase().includes(q) || tm.es.toLowerCase().includes(q);
  });

  // Client-side email validation (mirrors backend rules)
  const DISPOSABLE = new Set(['mailinator.com','tempmail.com','guerrillamail.com','10minutemail.com','yopmail.com','throwaway.email','temp-mail.org','sharklasers.com','trashmail.com','dispostable.com','maildrop.cc','mailnesia.com','burnermail.io','fakeinbox.com','guerrillamail.net','guerrillamail.de','guerrillamail.info']);
  const BLOCKED_EMAILS = new Set(['test@test.com','test123@test.com','test@example.com','abc@abc.com','hello@hello.com','fake@fake.com','admin@admin.com','example@example.com','demo@demo.com','user@user.com','mail@mail.com','test@gmail.com','test@yahoo.com','test@hotmail.com','asdf@asdf.com','aaa@aaa.com','spam@spam.com','no@no.com','none@none.com']);
  const BLOCKED_LOCAL_RE = /^(test\d*|fake\d*|demo\d*|example\d*|sample\d*|admin\d*|user\d*|noreply|asdf+|qwerty|aaa+|xxx+|abc\d*|xyz\d*|foo|bar|null|temp\d*)$/;

  const isEmailValid = (email) => {
    if (!email) return false;
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!re.test(email)) return false;
    const lower = email.toLowerCase();
    if (BLOCKED_EMAILS.has(lower)) return false;
    const [local, domain] = lower.split('@');
    if (BLOCKED_LOCAL_RE.test(local)) return false;
    if (DISPOSABLE.has(domain)) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!form.firstName.trim()) return;
    if (!isEmailValid(form.email)) { setEmailErr(t.emailError); return; }
    setEmailErr('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/predictions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_id: selected.id, team_name: teamName(selected), first_name: form.firstName, country: form.country, email: form.email }),
      });
      if (res.status === 422) { setEmailErr(t.emailError); setLoading(false); return; }
      const data = await res.json();
      if (data.results) setResults(data.results);
      setStep(3);
      trackEvent('predictor_completed', { language: lang, team: selected?.id });
      trackEvent('email_capture', { language: lang, source: 'predictor' });
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // Check for payment return on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const sessionId = params.get('session_id');
    if (payment === 'success' && sessionId) {
      setSupportStatus('processing');
      pollPaymentStatus(sessionId, 0);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (payment === 'cancelled') {
      setSupportStatus('cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts) => {
    if (attempts >= 6) { setSupportStatus('success'); return; }
    try {
      const res = await fetch(`${API}/api/supporters/checkout/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === 'paid') {
        setSupportStatus('success');
        fetch(`${API}/api/supporters/stats`).then(r => r.json()).then(setSupportStats).catch(() => {});
      } else if (data.status === 'expired') {
        setSupportStatus('cancelled');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (e) {
      setSupportStatus('success');
      trackEvent('supporter_payment_completed', { language: lang });
    }
  };

  const handleSupport = async (packageId, amt) => {
    setSupportDone(true);
    setSupportStatus('redirecting');
    trackEvent('supporter_payment_started', { language: lang, package: packageId, amount: amt || packageId });
    try {
      const body = { package_id: packageId, origin_url: window.location.origin, country: form.country };
      if (packageId === 'custom') body.custom_amount = amt;
      const res = await fetch(`${API}/api/supporters/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else { setSupportStatus('cancelled'); }
    } catch (e) { setSupportStatus('cancelled'); }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.origin + '/predictor' : '';
  const shareTeamText = selected ? t.shareText(teamName(selected)) : '';

  return (
    <div style={{ background: C.bg, color: C.text, minHeight: '100vh', overflowX: 'hidden' }}>
      <style>{CSS}</style>

      {/* ─── Ticker ─── */}
      <div style={{ height: 28, background: C.surface, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px' }}>
        <a href={`/world-cup-brand-index?lang=${lang}`} style={{ ...mono(500), fontSize: 8, color: C.blue, letterSpacing: '0.1em', textDecoration: 'none' }}>YBV SPORTS</a>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', borderRadius: 3, overflow: 'hidden', border: `1px solid ${C.border}` }} data-testid="lang-toggle">
            {['en', 'es'].map(l => (
              <button key={l} className="pred-lang" onClick={() => setLang(l)} data-testid={`lang-${l}`}
                style={{ ...mono(600), fontSize: 7, padding: '1px 6px', border: 'none', cursor: 'pointer', letterSpacing: '0.08em',
                  background: lang === l ? C.blue : 'transparent', color: lang === l ? '#fff' : C.muted, transition: 'all .15s' }}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: C.green, animation: 'pulse-dot 2s infinite' }} />
          <span style={{ ...mono(), fontSize: 7, color: C.green }}>LIVE</span>
        </div>
      </div>

      {/* ─── Hero ─── */}
      <section className="pred-fade" style={{ textAlign: 'center', padding: '48px 20px 32px', maxWidth: 640, margin: '0 auto' }}>
        <div style={{ ...mono(500), fontSize: 8, color: C.blue, border: `1px solid ${C.blue}`, background: C.blueDim, padding: '4px 14px', borderRadius: 20, letterSpacing: '0.12em', display: 'inline-block', marginBottom: 24 }}>
          {t.badge}
        </div>
        <h1 style={{ ...syne(800), fontSize: 'clamp(22px, 4.5vw, 34px)', lineHeight: 1.15, color: '#fff', margin: 0 }}>
          {t.h1a} <span style={{ color: C.blue }}>{t.h1b}</span> {t.h1c}
        </h1>
        <p style={{ ...sans(), fontSize: 13, color: C.muted, maxWidth: 460, margin: '16px auto 0', lineHeight: 1.6 }}>
          {t.note}
        </p>
      </section>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 20px 48px' }}>

        {/* Payment return banner */}
        {supportStatus && step !== 3 && (
          <div className="pred-fade" style={{ marginBottom: 16, padding: '14px 18px', borderRadius: 10, textAlign: 'center',
            background: supportStatus === 'success' ? C.greenDim : supportStatus === 'processing' ? C.amberDim : C.surface,
            border: `1px solid ${supportStatus === 'success' ? C.green : supportStatus === 'processing' ? C.amber : C.border}` }}>
            {supportStatus === 'processing' && <p style={{ ...sans(), fontSize: 13, color: C.amber, margin: 0 }}>{t.supportProcessing}</p>}
            {supportStatus === 'success' && <p style={{ ...sans(), fontSize: 13, color: C.green, margin: 0 }}>{t.supportSuccess}</p>}
            {supportStatus === 'cancelled' && (
              <p style={{ ...sans(), fontSize: 13, color: C.muted, margin: 0 }}>{t.supportCancelled}</p>
            )}
          </div>
        )}

        {/* ═══ Step 1: Team Selection ═══ */}
        {step === 1 && (
          <div className="pred-fade">
            <div style={{ ...mono(500), fontSize: 9, color: C.muted, letterSpacing: '0.08em', marginBottom: 12 }}>{t.selectTeam}</div>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={t.searchPlaceholder} data-testid="team-search"
              style={{ width: '100%', padding: '10px 14px', background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, ...sans(), fontSize: 13, marginBottom: 12, boxSizing: 'border-box', outline: 'none' }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 6 }}>
              {filtered.map(tm => (
                <button key={tm.id} className="pred-card" data-testid={`team-${tm.id}`} onClick={() => { setSelected(tm); setStep(2); trackEvent('predictor_started', { language: lang, team: tm.id }); }}
                  style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 8px', cursor: 'pointer', textAlign: 'center', transition: 'all .15s',
                    display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-start' }}>
                  <span style={{ fontSize: 22 }}>{tm.flag}</span>
                  <span style={{ ...sans(500), fontSize: 12, color: C.text }}>{teamName(tm)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ═══ Step 2: Form ═══ */}
        {step === 2 && selected && (
          <div className="pred-fade" style={{ maxWidth: 400, margin: '0 auto' }}>
            <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', ...mono(), fontSize: 10, marginBottom: 16 }}>{t.back}</button>
            <div style={{ background: C.surface, border: `1px solid ${C.blue}`, borderRadius: 12, padding: '20px', textAlign: 'center', marginBottom: 20 }}>
              <span style={{ fontSize: 40 }}>{selected.flag}</span>
              <div style={{ ...syne(700), fontSize: 18, color: '#fff', marginTop: 6 }}>{teamName(selected)}</div>
            </div>
            <div style={{ ...mono(500), fontSize: 9, color: C.muted, letterSpacing: '0.08em', marginBottom: 12 }}>{t.stepForm}</div>
            {[
              ['firstName', t.firstName, 'text'],
              ['country', t.country, 'text'],
              ['email', t.email, 'email'],
            ].map(([key, label, type]) => (
              <div key={key} style={{ marginBottom: 10 }}>
                <div style={{ ...mono(), fontSize: 9, color: C.muted, marginBottom: 4 }}>{label}</div>
                <input type={type} value={form[key]} onChange={e => { setForm({ ...form, [key]: e.target.value }); if (key === 'email') setEmailErr(''); }} data-testid={`form-${key}`}
                  style={{ width: '100%', padding: '10px 12px', background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, ...sans(), fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
              </div>
            ))}
            <button onClick={handleSubmit} disabled={loading || !form.firstName.trim() || !form.email.includes('@')} data-testid="submit-prediction"
              style={{ width: '100%', padding: '13px', ...mono(600), fontSize: 12, letterSpacing: '0.06em', color: '#fff',
                background: loading ? C.dim : C.blue, border: 'none', borderRadius: 8, cursor: loading ? 'not-allowed' : 'pointer', marginTop: 8, transition: 'background .15s' }}>
              {loading ? t.submitting : t.submit}
            </button>
            {emailErr && <p style={{ ...sans(), fontSize: 11, color: C.red, marginTop: 6, textAlign: 'center' }} data-testid="email-error">{emailErr}</p>}
          </div>
        )}

        {/* ═══ Step 3: Results ═══ */}
        {step === 3 && selected && (
          <div className="pred-fade">
            {/* Confirmation */}
            <div style={{ background: C.greenDim, border: `1px solid ${C.green}`, borderRadius: 12, padding: '24px', textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>✓</div>
              <div style={{ ...syne(700), fontSize: 18, color: '#fff', marginBottom: 4 }}>{t.confirmTitle}</div>
              <div style={{ ...sans(), fontSize: 13, color: C.muted }}>
                {t.confirmSub} <span style={{ color: '#fff', fontWeight: 600 }}>{selected.flag} {teamName(selected)}</span> {t.confirmAs}
              </div>
            </div>

            {/* Community Counters */}
            {results && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 24 }}>
                {[
                  [results.total_predictions, t.counterTotal, C.blue],
                  [results.countries_count, t.counterCountries, C.green],
                  [results.most_predicted, t.counterMost, C.amber],
                  [results.fastest_rising, t.counterFastest, C.blueL],
                ].map(([val, label, color], i) => (
                  <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px', textAlign: 'center' }}>
                    <div style={{ ...syne(800), fontSize: typeof val === 'number' ? 28 : 16, color, lineHeight: 1 }}>{val}</div>
                    <div style={{ ...mono(), fontSize: 8, color: C.muted, marginTop: 4, letterSpacing: '0.06em' }}>{label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Live Ranking */}
            {results && results.ranking.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ ...mono(500), fontSize: 9, color: C.muted, letterSpacing: '0.08em', marginBottom: 10 }}>{t.rankTitle}</div>
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
                  {results.ranking.map((r, i) => {
                    const barColor = i === 0 ? C.blue : i === 1 ? C.green : i === 2 ? C.amber : C.muted;
                    const maxPct = results.ranking[0]?.pct || 1;
                    return (
                      <div key={`${r.team_id}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: i < results.ranking.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                        <div style={{ ...mono(600), fontSize: 11, color: C.dim, width: 16 }}>{i + 1}</div>
                        <div style={{ ...sans(600), fontSize: 13, color: '#fff', flex: 1 }}>{r.team_name}</div>
                        <div style={{ flex: 2, height: 6, background: C.dim, borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${(r.pct / maxPct) * 100}%`, height: '100%', background: barColor, borderRadius: 3, transition: 'width .5s ease' }} />
                        </div>
                        <div style={{ ...mono(500), fontSize: 11, color: barColor, width: 36, textAlign: 'right' }}>{r.pct}%</div>
                        <div style={{ ...mono(), fontSize: 9, color: C.muted, width: 48, textAlign: 'right' }}>{r.votes} {t.rankVotes}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Social Sharing */}
            <div style={{ marginBottom: 32 }}>
              <div style={{ ...mono(500), fontSize: 9, color: C.muted, letterSpacing: '0.08em', marginBottom: 10 }}>{t.shareTitle}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { label: t.shareLinkedin, bg: '#0A66C2', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
                  { label: t.shareX, bg: '#1D9BF0', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTeamText)}&url=${encodeURIComponent(shareUrl)}` },
                  { label: t.shareWhatsapp, bg: '#25D366', href: `https://wa.me/?text=${encodeURIComponent(shareTeamText + '\n' + shareUrl)}` },
                ].map(s => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="pred-share" data-testid={`share-${s.label.toLowerCase()}`}
                    onClick={() => trackEvent('share_clicked', { language: lang, platform: s.label, source: 'predictor' })}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', background: s.bg, color: '#fff', borderRadius: 8, textDecoration: 'none', ...mono(600), fontSize: 11, letterSpacing: '0.04em', transition: 'all .15s' }}>
                    {s.label}
                  </a>
                ))}
              </div>
            </div>

            {/* ═══ Founding Supporters ═══ */}
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 32, marginBottom: 24 }}>
              {/* Founders */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '24px', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <img src="/founders.jpg" alt="Tamara & Alba Navarrete" style={{ width: 110, height: 110, borderRadius: 12, objectFit: 'cover', border: `2px solid ${C.border}`, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ ...syne(700), fontSize: 16, color: '#fff', marginBottom: 8 }}>{t.foundersTitle}</div>
                    {t.foundersBody.split('\n\n').map((p, i) => (
                      <p key={i} style={{ ...sans(), fontSize: 13, color: C.muted, lineHeight: 1.65, margin: i === 0 ? 0 : '8px 0 0' }}>{p}</p>
                    ))}
                  </div>
                </div>
              </div>

              {/* Support CTA */}
              <div style={{ background: C.surface, border: `1px solid ${C.green}`, borderRadius: 12, padding: '24px', textAlign: 'center' }}>
                <div style={{ ...syne(700), fontSize: 16, color: '#fff', marginBottom: 6 }}>🚀 {t.supportTitle}</div>
                <p style={{ ...sans(), fontSize: 13, color: C.muted, lineHeight: 1.6, maxWidth: 420, margin: '0 auto 16px' }}>{t.supportBody}</p>

                {!supportDone ? (
                  <div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {[1, 5, 10].map(amt => (
                        <button key={amt} className="pred-amt" onClick={() => handleSupport(`support_${amt}`)} data-testid={`support-${amt}`}
                          style={{ padding: '10px 20px', background: 'transparent', border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, cursor: 'pointer', ...mono(600), fontSize: 14, transition: 'all .15s' }}>
                          €{amt}
                        </button>
                      ))}
                      <button className="pred-amt" onClick={() => setShowCustom(!showCustom)} data-testid="support-other"
                        style={{ padding: '10px 20px', background: 'transparent', border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, cursor: 'pointer', ...mono(600), fontSize: 12, transition: 'all .15s' }}>
                        {t.supportOther}
                      </button>
                    </div>
                    {showCustom && (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
                        <input type="number" min="1" step="1" value={customAmount} onChange={e => setCustomAmount(e.target.value)} placeholder={t.supportCustom} data-testid="support-custom-input"
                          style={{ width: 140, padding: '8px 10px', background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, ...mono(), fontSize: 13, textAlign: 'center' }} />
                        <button className="pred-amt" onClick={() => { const a = parseFloat(customAmount); if (a > 0) handleSupport('custom', a); }} data-testid="support-custom-submit"
                          style={{ padding: '8px 16px', background: C.green, border: 'none', color: '#fff', borderRadius: 6, cursor: 'pointer', ...mono(600), fontSize: 12 }}>
                          €{customAmount || '?'} →
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ padding: '8px 0', textAlign: 'center' }} data-testid="support-status">
                    {supportStatus === 'redirecting' && <p style={{ ...sans(), fontSize: 13, color: C.amber }}>{t.supportThanks}</p>}
                    {supportStatus === 'processing' && <p style={{ ...sans(), fontSize: 13, color: C.amber }}>{t.supportProcessing}</p>}
                    {supportStatus === 'success' && <p style={{ ...sans(), fontSize: 13, color: C.green }}>{t.supportSuccess}</p>}
                    {supportStatus === 'cancelled' && (
                      <div>
                        <p style={{ ...sans(), fontSize: 13, color: C.muted, marginBottom: 8 }}>{t.supportCancelled}</p>
                        <button onClick={() => { setSupportDone(false); setSupportStatus(null); }}
                          style={{ padding: '6px 16px', background: 'transparent', border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, cursor: 'pointer', ...mono(), fontSize: 11 }}>
                          {t.supportOther} →
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {supportStats.total_supporters > 0 && (
                  <div style={{ ...mono(), fontSize: 9, color: C.muted, marginTop: 14 }}>
                    🌍 {t.supportProof(supportStats.total_supporters, supportStats.countries_count || 1)}
                  </div>
                )}
                <p style={{ ...sans(), fontSize: 9, color: C.dimBorder || '#3A3F46', marginTop: 16, lineHeight: 1.5, maxWidth: 400, margin: '16px auto 0' }}>{t.supportLegal}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Footer ─── */}
      <footer style={{ background: C.surface, borderTop: `1px solid ${C.border}`, padding: '20px', textAlign: 'center' }}>
        <div style={{ ...mono(), fontSize: 9, color: C.dim }}>{t.footerCopy}</div>
        <p style={{ ...sans(), fontSize: 9, color: '#2A2F36', maxWidth: 600, margin: '12px auto 0', lineHeight: 1.6 }}>{t.disclaimer}</p>
      </footer>
    </div>
  );
}
